import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import * as Icons from "lucide-react";
import { Menu, X, ChevronDown, LogIn } from "lucide-react";
import { NAV_LINKS, RESOURCES, PLATFORM, FREE_TRIAL_URL, LOGIN_URL } from "../mock";
import Logo from "./Logo";

const MENUS = {
  PLATFORM: { items: PLATFORM, active: ["/platform"], width: "w-[360px]" },
  RESOURCES: { items: RESOURCES, active: ["/blog", "/case-studies"], width: "w-[340px]" },
};

/* Resource item used in both the desktop dropdown and the mobile menu */
const ResourceItem = ({ r, onClick, compact = false }) => {
  const Icon = Icons[r.icon] || Icons.Link;
  const inner = (
    <>
      <span className="w-9 h-9 rounded-xl bg-[var(--fr-primary-tint)] text-[var(--fr-primary-dark)] flex items-center justify-center flex-shrink-0">
        <Icon size={17} />
      </span>
      <span className="min-w-0">
        <span className="flex items-center gap-2 text-[14px] font-semibold text-[var(--fr-ink)]">
          {r.label}
          {r.soon && <span className="chip !py-0 !px-2 !text-[10px] uppercase tracking-wide">Soon</span>}
        </span>
        {!compact && <span className="block text-[12.5px] text-[var(--fr-muted)] leading-snug mt-0.5">{r.text}</span>}
      </span>
    </>
  );
  const cls = "flex items-start gap-3 rounded-xl p-3 hover:bg-[var(--fr-bg-alt)] transition-colors";
  return r.href ? (
    <a href={r.href} target={r.external ? "_blank" : undefined} rel="noreferrer" className={cls} onClick={onClick} data-testid={`resource-${r.label.toLowerCase().replace(/\s+/g, "-")}`}>
      {inner}
    </a>
  ) : (
    <Link to={r.to} className={cls} onClick={onClick} data-testid={`resource-${r.label.toLowerCase().replace(/\s+/g, "-")}`}>
      {inner}
    </Link>
  );
};

const NavDropdown = ({ label, menu }) => {
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const { pathname } = useLocation();
  const { items, active: prefixes, width } = MENUS[menu];
  const active = prefixes.some((p) => pathname.startsWith(p));
  const key = label.toLowerCase();

  const show = () => {
    clearTimeout(timer.current);
    setOpen(true);
  };
  const hide = () => {
    timer.current = setTimeout(() => setOpen(false), 120);
  };

  useEffect(() => setOpen(false), [pathname]);

  return (
    <div className="relative" onMouseEnter={show} onMouseLeave={hide}>
      <button
        type="button"
        className={`nav-link inline-flex items-center gap-1 ${active ? "active" : ""}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        data-testid={`nav-link-${key}`}
      >
        {label} <ChevronDown size={14} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      <div
        className={`absolute left-1/2 -translate-x-1/2 top-full pt-3 ${width} transition-all duration-200 origin-top ${
          open ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-1 pointer-events-none"
        }`}
        role="menu"
        data-testid={`${key}-menu`}
      >
        <div className="card p-2 shadow-[0_24px_48px_-24px_rgba(14,14,16,0.3)]">
          {items.map((r) => (
            <ResourceItem key={r.label} r={r} />
          ))}
        </div>
      </div>
    </div>
  );
};

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [groupOpen, setGroupOpen] = useState({ PLATFORM: true, RESOURCES: true });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      className={`sticky top-0 z-40 nav-blur border-b transition-colors duration-200 ${
        scrolled ? "border-[var(--fr-border)]" : "border-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="container-x h-[64px] flex items-center justify-between">
        <Logo height={26} />

        <nav className="hidden md:flex items-center gap-1" aria-label="Main">
          {NAV_LINKS.map((l) =>
            l.children ? (
              <NavDropdown key={l.label} label={l.label} menu={l.children} />
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
              >
                {l.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="flex items-center gap-1 md:gap-2">
          <a
            href={LOGIN_URL}
            target="_blank"
            rel="noreferrer"
            className="nav-link inline-flex items-center gap-1.5 md-only"
            data-testid="nav-login"
          >
            <LogIn size={15} />
            Log in
          </a>
          <a href={FREE_TRIAL_URL} target="_blank" rel="noreferrer" className="btn btn-primary btn-sm md-only" data-testid="nav-cta">
            Start free trial
          </a>
          <button
            type="button"
            className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-[var(--fr-border)] bg-white"
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          className="md:hidden absolute top-full left-0 right-0 h-[calc(100vh-64px)] bg-[var(--fr-bg)] z-40 overflow-y-auto border-t border-[var(--fr-border)]"
          data-testid="nav-mobile-menu"
        >
          <div className="container-x py-6 flex flex-col gap-1">
            {NAV_LINKS.map((l, i) =>
              l.children ? (
                <div key={l.label} className="border-b border-[var(--fr-border)]">
                  <button
                    type="button"
                    onClick={() => setGroupOpen((g) => ({ ...g, [l.children]: !g[l.children] }))}
                    className="w-full flex items-center justify-between font-display text-2xl font-semibold py-3 text-[var(--fr-ink)]"
                    aria-expanded={groupOpen[l.children]}
                    data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                  >
                    {l.label} <ChevronDown size={20} className={`transition-transform ${groupOpen[l.children] ? "rotate-180" : ""}`} />
                  </button>
                  {groupOpen[l.children] && (
                    <div className="pb-3 -mx-3">
                      {MENUS[l.children].items.map((r) => (
                        <ResourceItem key={r.label} r={r} compact onClick={() => setOpen(false)} />
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `font-display text-2xl font-semibold py-3 border-b border-[var(--fr-border)] fade-up delay-${Math.min(i + 1, 4)} ${
                      isActive ? "text-[var(--fr-primary-dark)]" : "text-[var(--fr-ink)]"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              )
            )}
            <a
              href={FREE_TRIAL_URL}
              target="_blank"
              rel="noreferrer"
              onClick={() => setOpen(false)}
              className="btn btn-primary btn-lg mt-6 w-full"
              data-testid="nav-mobile-cta"
            >
              Start free trial
            </a>
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="btn btn-outline btn-lg mt-3 w-full"
              data-testid="nav-mobile-demo"
            >
              Request a demo
            </Link>
            <a
              href={LOGIN_URL}
              target="_blank"
              rel="noreferrer"
              onClick={() => setOpen(false)}
              className="mt-5 inline-flex items-center justify-center gap-2 text-sm font-semibold text-[var(--fr-ink-2)] hover:text-[var(--fr-ink)]"
              data-testid="nav-mobile-login"
            >
              <LogIn size={16} />
              Log in to Fleetroot
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
